import {
  Table,
  Skeleton,
  styled,
  Pagination,
  Paper,
  Stack
} from "@mui/material";
import { PropsWithChildren } from "react";

import { ASTableV2Body } from "./ASTableV2Body";
import { ASTableV2Cell } from "./ASTableV2Cell";
import { ASTableV2DatePicker } from "./ASTableV2DatePicker";
import { ASTableV2Row } from "./ASTableV2Row";
import { ASTableV2SearchAndFilter } from "./ASTableV2SearchAndFilter";

const StyledASTableV2 = styled(Table, {
  shouldForwardProp: (propName) =>
    !["gridTemplateColumns", "gridTemplateRows"].includes(propName as string)
})<{
  gridTemplateColumns: string;
  gridTemplateRows?: string;
}>(({ gridTemplateColumns, gridTemplateRows, theme: { spacing } }) => ({
  flexGrow: 1,
  display: "grid",
  gridTemplateColumns,
  gridTemplateRows,
  padding: spacing(1),
  overflowY: "auto"
}));

const TableContainer = styled(Paper)(({ theme: { palette } }) => ({
  backgroundColor: palette.gray.light,
  height: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column"
}));

type ASTableV2Props = {
  gridTemplateColumns: string;
  gridTemplateRows?: string;
  isLoading: boolean;
  stickyHeader?: boolean;
  containerSx?: React.CSSProperties;
  pagination?: {
    setPage: (page: number) => void;
    page: number;
    pageCount: number;
    disabled?: boolean;
  };
  searchComponent?: React.ReactNode;
  filterComponent?: React.ReactNode;
  datePicker?: {
    selectedDate: Date | null;
    onDateChange: (date: Date | null) => void;
  };
  toolbarStart?: React.ReactNode;
} & PropsWithChildren;

export const ASTableV2 = ({
  gridTemplateColumns,
  gridTemplateRows,
  isLoading,
  stickyHeader = false,
  containerSx,
  pagination,
  children,
  searchComponent,
  filterComponent,
  datePicker,
  toolbarStart
}: ASTableV2Props) => {
  const shouldShowToolbar =
    !!searchComponent || !!filterComponent || !!datePicker || !!toolbarStart;

  return (
    <TableContainer sx={{ ...(containerSx && { ...containerSx }) }}>
      {shouldShowToolbar && (
        <Stack
          sx={{
            pt: 1,
            px: 1,
            pb: 0,
            flexDirection: "row",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap"
          }}
          role="toolbar"
          aria-label="Table controls and filters"
        >
          <Stack
            sx={{
              flexDirection: "column",
              gap: 1,
              alignItems: "flex-start",
              minWidth: 0
            }}
          >
            {toolbarStart}
            <ASTableV2SearchAndFilter
              searchComponent={searchComponent}
              filterComponent={filterComponent}
            />
          </Stack>
          {datePicker && <ASTableV2DatePicker {...datePicker} />}
        </Stack>
      )}
      <StyledASTableV2
        gridTemplateColumns={gridTemplateColumns}
        gridTemplateRows={gridTemplateRows}
        stickyHeader={stickyHeader}
      >
        {children}
        {isLoading && (
          <ASTableV2Body>
            <ASTableV2Row>
              <ASTableV2Cell sx={{ gridColumn: "1 / -1", display: "flex" }}>
                <Skeleton
                  role="progressbar"
                  sx={{ flexGrow: 1 }}
                  variant="text"
                ></Skeleton>
              </ASTableV2Cell>
            </ASTableV2Row>
          </ASTableV2Body>
        )}
      </StyledASTableV2>
      {!!pagination && pagination.pageCount > 1 && (
        <Pagination
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 2
          }}
          count={pagination.pageCount}
          page={pagination.page}
          onChange={(_e, p) => pagination.setPage(p)}
          shape="rounded"
          disabled={pagination.disabled}
        />
      )}
    </TableContainer>
  );
};
